import { getEngine } from '@lio/rule-engine';
import { GroupedQuote, StructuredQuote } from '@store/Quote/Quote.types';
import { formatCurrency } from '@utils/number';

export const getQuoteTermRows = (structuredQuote: StructuredQuote) => {
  const ruleEngine = getEngine(structuredQuote as GroupedQuote);

  const policyQuoteItem = structuredQuote.quoteItems.find((qi) => qi.quoteItemType.codeName === 'Policy');
  const attributes = policyQuoteItem?.attributeTypes ?? {};

  if (!policyQuoteItem) {
    return [];
  }

  ruleEngine.setEngineInitialQuoteItem(policyQuoteItem.id);

  const genLiabilityLimits = [
    {
      id: 'occurrence-limit',
      title: 'Each Occurrence Limit',
      key: 'GLPremOpsProdsEachOccurrenceLimit',
      isActive: ruleEngine.execute(attributes['GLPremOpsProdsEachOccurrenceLimit'].isActive),
    },
    {
      id: 'aggregate-limit',
      title: 'General Aggregate Limit',
      key: 'GLGeneralAggregateLimit',
      isActive: ruleEngine.execute(attributes['GLGeneralAggregateLimit'].isActive),
    },
    {
      id: 'injury-limit',
      title: 'Personal & Advertising Injury Limit',
      key: 'GLPersonalAndAdvertisingInjuryLimit',
      isActive: ruleEngine.execute(attributes['GLPersonalAndAdvertisingInjuryLimit'].isActive),
    },
    {
      id: 'products-limit',
      title: 'Products/Completed Aggregate',
      key: 'GLProductsCompletedOperationsAggregateLimit',
      isActive: ruleEngine.execute(attributes['GLProductsCompletedOperationsAggregateLimit'].isActive),
    },
    {
      id: 'premises-limit',
      title: 'Damage to Premises Rented to You',
      key: 'GLGeneralLiabilityDamageToPremisesRentedToYouCoverageLimit',
      isActive: ruleEngine.execute(attributes['GLGeneralLiabilityDamageToPremisesRentedToYouCoverageLimit'].isActive),
    },
    {
      id: 'medical-payments-limit',
      title: 'Medical Payments',
      key: 'GLMedicalPaymentLimit',
      notIncludedValue: 'Not Included',
      isActive: ruleEngine.execute(attributes['GLMedicalPaymentLimit'].isActive),
    },
    {
      id: 'auto-limit',
      title: 'Hired, Non-owned Auto',
      notIncludedValue: 'Not Included',
      key: 'GLHiredAutoNonOwnedLiabilityLimit',
      isActive: ruleEngine.execute(attributes['GLHiredAutoNonOwnedLiabilityLimit'].isActive),
    },
  ]?.filter((x) => x.isActive || x.notIncludedValue);

  const SAMLimits = [
    {
      id: 'sam-occurrence-limit',
      title: 'Per Occurrence Limit',
      key: 'SAMEachActLimit',
      isActive: ruleEngine.execute(attributes['SAMEachActLimit'].isActive),
    },
    {
      id: 'sam-aggregate-limit',
      title: 'Aggregate Limit',
      key: 'SAMGeneralAggregateLimit',
      isActive: ruleEngine.execute(attributes['SAMGeneralAggregateLimit'].isActive),
    },
  ]?.filter((x) => x.isActive);

  const inlandMarineLimits = [
    {
      id: 'inland-marine-limit',
      title: 'Limit (non-scheduled items)',
      key: 'IMMiscellaneousArticlesLimit',
      isActive: ruleEngine.execute(attributes['IMMiscellaneousArticlesLimit'].isActive),
    },
  ]?.filter((x) => x.isActive);

  const excessLimits = [
    {
      id: 'excess-limit',
      title: 'Per Occurrence limit',
      key: 'EXOccurrenceLimit',
      isActive: ruleEngine.execute(attributes['EXOccurrenceLimit'].isActive),
    },
    {
      id: 'excess-aggregate-limit',
      title: 'Aggregate Limit',
      key: 'EXAggregateLimit',
      isActive: ruleEngine.execute(attributes['EXAggregateLimit'].isActive),
    },
  ]?.filter((x) => x.isActive);

  return [
    { sectionHeader: true, id: 'terms-conditions', title: 'Quote Terms & Conditions', value: '' },
    { items: genLiabilityLimits, titleRow: true, id: 'gl-header', title: 'General Liability', value: '' },
    ...genLiabilityLimits.map(({ id, title, key, isActive, notIncludedValue }) => {
      return {
        id,
        title,
        value: !isActive
          ? notIncludedValue
          : formatCurrency((attributes[key]?.attribute?.valueOverride ?? attributes[key]?.attribute?.value) as string, {
              minimumPrecision: 0,
              maximumPrecision: 0,
            }),
      };
    }),
    { items: SAMLimits, titleRow: true, id: 'sam-header', title: 'Sexual Abuse or Sexual Molestation', value: '' },
    ...SAMLimits.map(({ id, title, key }) => ({
      id,
      title,
      value: formatCurrency(
        (attributes[key]?.attribute?.valueOverride ?? attributes[key]?.attribute?.value) as string,
        { minimumPrecision: 0, maximumPrecision: 0 },
      ),
    })),
    { items: inlandMarineLimits, titleRow: true, id: 'im-header', title: 'Inland Marine', value: '' },
    ...inlandMarineLimits.map(({ id, title, key }) => ({
      id,
      title,
      value: formatCurrency(
        (attributes[key]?.attribute?.valueOverride ?? attributes[key]?.attribute?.value) as string,
        { minimumPrecision: 0, maximumPrecision: 0 },
      ),
    })),
    { items: excessLimits, titleRow: true, id: 'excess-header', title: 'Excess', value: '' },
    ...excessLimits.map(({ id, title, key }) => ({
      id,
      title,
      value: formatCurrency(
        (attributes[key]?.attribute?.valueOverride ?? attributes[key]?.attribute?.value) as string,
        {
          minimumPrecision: 0,
          maximumPrecision: 0,
        },
      ),
    })),
  ]?.filter((x: any) => {
    if (x.titleRow) {
      return x.items.length > 0;
    }

    return true;
  });
};
